import { douglasPeuckerFilter } from "../algorithms/douglas-peucker-filter";
import { MultiMap } from "../multi-map";
import { Line } from "./line";
import { LineStringReadable } from "./line-string-readable";
import { Vec } from "./vec";
import { VecLike } from "./vec-like";

export class LineString<Point extends Vec> extends LineStringReadable<Point,Point[]>{
	constructor(
		points:Point[],
	){
		super(points);
	}

	protected newLine(a:Point, b:Point){
		return new Line<Point>(a,b);
	}

	private newOfThis(points:Point[]){
		return <this>new (Object.getPrototypeOf(this).constructor)(points);
	}

	public clone(){
		return this.newOfThis(this.points.map(p=>p.clone()));
	}

	public unshift(p:Point){
		return this.points.unshift(p);
	}

	public push(...items:Point[]){
		return this.points.push(...items);
	}

	//grabs a section of the line string kinda like String.substring does for strings
	//except it allows fractional indexes (blending between points) and the endIndex is inclusive
	public substring(beginIndex:number, endIndex:number){
		let reverse=beginIndex>endIndex;
		if(reverse)
			[beginIndex,endIndex]=[endIndex,beginIndex];
		if(beginIndex<0)
			beginIndex=0;
		if(endIndex>this.points.length-1)
			endIndex=this.points.length-1;

		const points:Point['vecType'][]=[];
		if(beginIndex===endIndex){
			points.push(this.atIndex(beginIndex,true));
		}else{
			const beginInner=Math.ceil(beginIndex);
			const endInner=Math.floor(endIndex);
			if(beginIndex<beginInner)
				points.push(this.points[beginInner-1].clone().lerp(this.points[beginInner],beginIndex%1));
			points.push(...this.points.slice(beginInner,endInner+1));
			if(endIndex>endInner)
				points.push(this.points[endInner].clone().lerp(this.points[endInner+1],endIndex%1));
		}
		if(reverse)
			points.reverse();
		return <this>new (Object.getPrototypeOf(this).constructor)(points);
	}

	public transform(transformer:(p:Point)=>Point){
		for(let i=0;i<this.points.length;++i){
			this.points[i]=transformer(this.points[i]);
		}
		return this;
	}

	public translate(delta:VecLike){
		for(const p of this.points)
			p.add(delta);
		return this;
	}

	public translateX(delta:number){
		for(const p of this.points)
			p.x+=delta;
		return this;
	}

	public translateY(delta:number){
		for(const p of this.points)
			p.y+=delta;
		return this;
	}

	public setX(x:number){
		for(const p of this.points)
			p.x=x;
		return this;
	}

	public setY(y:number){
		for(const p of this.points)
			p.y=y;
		return this;
	}

	public *segments(){
		for(const [a,b] of this.points.pairs()){
			yield this.newLine(a,b);
		}
	}

	public merge(
		that:this,
		epsilon:number,
	){
		if(this.points.length===0 || that.points.length===0){
			this.points=this.points.concat(that.points);
			return this;
		}
		const dists=[
			this.at(-1)!.distToSq(that.at(+0)!),
			this.at(-1)!.distToSq(that.at(-1)!),
			this.at(+0)!.distToSq(that.at(-1)!),
			this.at(+0)!.distToSq(that.at(+0)!),
		];
		let i=dists.lowestIndex();

		let pointsA:Point[];
		let pointsB:Point[];
		if(i<2){
			pointsA=this.points;
			pointsB=i===0?that.points:that.points.slice().reverse();
		}else{
			pointsA=i===2?that.points.slice():that.points.slice().reverse();
			pointsB=this.points;
		}
		if(dists[i]<epsilon**2)
			pointsA.pop();
		this.points=pointsA.concat(pointsB);
		return this;
	}

	public fragment(wantedInterval:number){
		this.points=LineString.fragment(this.points,false,wantedInterval);
		return this;
	}

	public resample(wantedInterval:number){
		if(this.count<2){
			this.points=this.points.map(p=>p.clone());
			return this;
		}
		const length=this.len();
		const intervalCount=Math.ceil(this.len()/wantedInterval);
		const intervalLength=length/intervalCount;

		const points:Point[]=[this.points[0].clone()];
		let stepDist=intervalLength;
		let aDist=0;
		let a=this.points[0];
		for(let i=1;i<this.points.length;++i){
			const b=this.points[i];
			const segDist=a.distTo(b);
			const bDist=aDist+segDist;
			while(stepDist<bDist && points.length<intervalCount){
				points.push(a.clone().lerp(b,(stepDist-aDist)/segDist));
				stepDist+=intervalLength;
			}
			a=b;
			aDist=bDist;
		}
		points.push(this.at(-1)!.clone());
		this.points=points;
		return this;
	}

	public douglasPeuckerFilter(threshold:number){
		this.points=douglasPeuckerFilter(this.points,p=>p,threshold);
		return this;
	}

	//its like douglasPeuckerFilter but the points are removed, only flatten on to the line that would be there if they were removed
	public douglasPeuckerFlatten(threshold:number){
		const {points}=this;
		const indices=douglasPeuckerFilter([...points.keys()],i=>points[i],threshold);
		for(const [ia,ic] of indices.pairs()){
			const a=points[ia];
			const c=points[ic];
			for(let ib=ia+1;ib<ic;++ib){
				const b=points[ib];
				b.blend(a,c,(ib-ia)/(ic-ia));
			}
		}
		return this;
	}

	public soften(iterations:number){
		const {points}=this;
		let tmp=points.map(p=>p.clone());
		for(let itIndex=0;itIndex<iterations;++itIndex){
			for(let i=1;i<tmp.length-1;++i)
				tmp[i].copy(points[i-1]).mean(points[i+1]);
			for(const [i,p] of points.entries())
				p.copy(tmp[i]);
		}
		return this;
	}

	//takes all the points and forces them into a line never moving farther away from the end point, point behind first point or past the last point are removed
	public forceLinear(){
		if(this.count<=2)
			return this;
		const a=this.at(0)!;
		const b=this.at(-1)!;
		const dir=b!.delta(a).nrm();
		const min=dir.dot(a);
		const max=dir.dot(b);

		let innerIndices=[...this.points.keys()].slice(1,this.points.length-1);
		const map=new Map<number,number>();
		for(const i of innerIndices){
			map.set(i,dir.dot(this.points[i]));
		}
		innerIndices=innerIndices.filter(p=>{
			const dist=map.get(p)!;
			return min<dist && dist<max;
		});

		innerIndices.sort((i,j)=>map.get(i)!-map.get(j)!);

		const points=[a];
		let prevIndex=0;
		for(const i of innerIndices){
			if(i<=prevIndex){
				const q=points[points.length-1].clone().mean(this.points[i]);
				points[points.length-1]=q;
			}else{
				prevIndex=i;
				points.push(this.points[i]);
			}
		}
		points.push(b);
		this.points=points;
		return this;
	}

	//forces dist of the points from a straight line formed by the first and last to be no steep than distanceAlong*factor
	public smoothAmplitude(factor:number){
		if(this.count<=2)
			return this;
		const line=this.newLine(this.at(0)!,this.at(-1)!);
		let a=this.points[0];
		let aDist=0;
		let along=0;
		for(let i=1;i+1<this.points.length;++i){
			const b=this.points[i];
			const step=Math.abs(line.distOf(b)-along);
			const bcp=line.closestPoint(b);
			let bDist=b.distTo(bcp);
			const maxDist=step*factor+aDist;
			if(bDist>maxDist){
				b.lerp(bcp,1-maxDist/bDist);
				bDist=maxDist;
			}
			a=b;
			aDist=bDist;
			along+=step;
		}
		line.reverse();
		a=this.points[this.points.length-1];
		aDist=0;
		along=0;
		for(let i=this.points.length-2;i>=0;--i){
			const b=this.points[i];
			const step=Math.abs(line.distOf(b)-along);
			const bcp=line.closestPoint(b);
			let bDist=b.distTo(bcp);
			const maxDist=step*factor+aDist;
			if(bDist>maxDist){
				b.lerp(bcp,1-maxDist/bDist);
				bDist=maxDist;
			}
			a=b;
			aDist=bDist;
			along+=step;
		}
		return this;
	}
}

export namespace LineString{

	export interface AssemblableLine<Point extends Vec>{
		at(i:number):Point;
		[Symbol.iterator]():Iterator<Point>;
	}

	class EplisonSortedPointList<Point extends Vec>{
		public constructor(
			lines:Iterable<AssemblableLine<Point>>,
			public readonly epsilon:number,
			private readonly getSortValue:(p:Point)=>number,
		){
			const {map}=this;
			for(const line of lines){
				for(const i of <(0|-1)[]>[0,-1])
					map.add(Math.floor(getSortValue(line.at(i))/epsilon),[line,i]);
			}
		}

		private readonly map=new MultiMap<number,[AssemblableLine<Point>,0|-1]>();

		public getNear(p:Point){
			const key=Math.floor(this.getSortValue(p)/this.epsilon);
			const a=(this.map.get(key) ?? []);
			const b=(this.map.get(key-1) ?? []);
			const c=(this.map.get(key+1) ?? []);
			return a.concat(b).concat(c);
		}
	}


	export function _assembleFromLines<Point extends Vec>(
		_lines:AssemblableLine<Point>[],
		epsilon:number,
		getSortValue:(p:Point)=>number,
	){
		const lines=new Set(_lines);
		if(lines.size===0)
			return [];

		const map=new EplisonSortedPointList(lines,epsilon,getSortValue);

		const epsilonSq=epsilon**2;
		const lineStrings:Point[][]=[];
		for(let _ls of lines){
			lines.delete(_ls);
			const ls=[..._ls];

			while(true){
				let foundDist=Infinity;
				let found:[AssemblableLine<Point>,number,0|-1];
				for(let lsi=-1;lsi<=0;++lsi){
					const nearLines=map.getNear(ls.at(lsi)!);
					for(const [line,li] of nearLines){
						if(lines.has(line)){
							const dist=ls.at(lsi)!.distToSq(line.at(li));
							if(foundDist>dist){
								foundDist=dist;
								found=[line,lsi,li];
							}
						}
					}
				}
				if(foundDist>=epsilonSq)
					break;
				let [line,lsi,li]=found!;
				lines.delete(line);

				//const p=(li==='a'?line['b']:line['a']);
				const points=[...line];
				if(lsi<0){
					if(li<0)
						points.reverse();
					ls.pop();
					ls.push(...points);
				}else{
					if(li>=0)
						points.reverse();
					points.pop();
					ls.unshift(...points);
				}
			}
			lineStrings.push(ls);
		}
		return lineStrings;
	}
}
